import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from "@lemonadetraining/web-ui";
import { IconPlus, IconX } from "@tabler/icons-react";
import getYouTubeID from "get-youtube-id";
import {
  WORKOUT_CATEGORIES,
  WORKOUT_NOTE_MAX,
  type Movement,
  type WorkoutData,
} from "@lemonadetraining/contracts";

import MovementItem from "../../Workout/MovementItem";
import { youTubeWatchURL } from "../../WorkoutCard/AddYouTube";
import { isPermissionDenied } from "../../../lib/callableError";
import { MAX_KINDS, kindLabel, kindValue } from "../../../lib/workoutKinds";
import WorkoutPartView, { isBlankMovement } from "./WorkoutPartView";

/** The planner holds dates as a JS Date; stored cards carry a Timestamp. */
export type EditorWorkoutData = Omit<WorkoutData, "date"> & {
  date?: WorkoutData["date"] | Date | null;
};

export interface WorkoutEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  initial: EditorWorkoutData;
  placement: string;
  placementControl?: React.ReactNode;
  /** The placement was moved outside the draft; closing should still ask. */
  placementChanged?: boolean;
  programName: string;
  /** Private notes, kept apart from the card; absent where the viewer can't have them. */
  note?: { label: string; initial: string; status: "idle" | "loading" | "ready" | "error" };
  /** `note` is the notes text when the field is shown. A rejection with a string `userMessage` is shown as is. */
  onSave: (data: EditorWorkoutData, note?: string) => Promise<void>;
}

export const MAX_VIDEOS = 6;

const INVALID_LINK =
  "This is not a YouTube link. Copy the link from YouTube, it looks like https://youtu.be/… or https://www.youtube.com/watch?v=…";

interface MovementRow {
  key: number;
  movement: Movement;
  fromInitial: boolean;
}

interface VideoRow {
  key: number;
  text: string;
  /** The stored ID this row was opened with; cleared on edit so the text is parsed instead. */
  seededID: string | null;
}

const canonical = (tag: string) => kindValue(tag) ?? tag;

const sameMovement = (a: Movement, b: Movement) =>
  (a.movementRegistryID ?? "") === (b.movementRegistryID ?? "") &&
  a.sets === b.sets &&
  a.reps === b.reps &&
  (a.percent ?? 0) === (b.percent ?? 0);

const mergeMovement = (prev: Movement, next: Movement): Movement => {
  const merged: Movement = { ...prev, movementRegistryID: next.movementRegistryID ?? "" };
  if (next.sets === undefined) delete merged.sets;
  else merged.sets = next.sets;
  if (next.reps === undefined) delete merged.reps;
  else merged.reps = next.reps;
  // MovementItem reports an unset load as 0; don't invent a field the card never had.
  if (next.percent === undefined || (next.percent === 0 && prev.percent === undefined)) {
    delete merged.percent;
  } else merged.percent = next.percent;
  return merged;
};

const parseVideos = (rows: VideoRow[]) => {
  const ids: string[] = [];
  const errors: Record<number, string> = {};
  rows.forEach((row) => {
    if (row.seededID !== null) {
      ids.push(row.seededID);
      return;
    }
    const text = row.text.trim();
    if (text === "") return;
    const parsed = getYouTubeID(text);
    if (parsed) ids.push(parsed);
    else errors[row.key] = INVALID_LINK;
  });
  return { ids, errors };
};

type CloseRef = React.MutableRefObject<() => void>;

const EditorContent: React.FC<WorkoutEditorProps & { closeRef: CloseRef }> = ({
  onOpenChange,
  mode,
  initial,
  placement,
  placementControl,
  placementChanged = false,
  programName,
  note,
  onSave,
  closeRef,
}) => {
  const uid = useId();
  const nextKey = useRef(0);
  const titleRef = useRef<HTMLInputElement>(null);
  const keepEditingRef = useRef<HTMLButtonElement>(null);

  const [kinds, setKinds] = useState<string[]>(() => [...(initial.tags ?? [])]);
  const [title, setTitle] = useState(initial.title ?? "");
  const [description, setDescription] = useState(initial.description ?? "");
  const noteReady = note?.status === "ready";
  const [noteText, setNoteText] = useState(noteReady ? (note?.initial ?? "") : "");
  const noteBase = useRef(noteReady ? (note?.initial ?? "") : "");
  const noteSeeded = useRef(noteReady);
  // Notes can arrive after the editor opened; until then the field is locked,
  // so filling it in now never overwrites anything the coach typed.
  useEffect(() => {
    if (!noteReady || noteSeeded.current) return;
    noteSeeded.current = true;
    noteBase.current = note?.initial ?? "";
    setNoteText(note?.initial ?? "");
  }, [noteReady, note?.initial]);
  const [movementRows, setMovementRows] = useState<MovementRow[]>(() =>
    (initial.movements ?? []).map((m) => ({
      key: nextKey.current++,
      movement: { ...m },
      fromInitial: true,
    }))
  );
  const [videoRows, setVideoRows] = useState<VideoRow[]>(() =>
    (initial.youTubeIDs ?? [])
      // Cards saved before links were validated can hold null or "": they never played.
      .filter((id): id is string => typeof id === "string" && id !== "")
      .map((id) => ({ key: nextKey.current++, text: youTubeWatchURL(id), seededID: id }))
  );
  const [titleError, setTitleError] = useState<string | null>(null);
  const [videoErrors, setVideoErrors] = useState<Record<number, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  const keptMovements = movementRows
    .filter((r) => r.fromInitial || !isBlankMovement(r.movement))
    .map((r) => ({ ...r.movement }));

  const snapshot = JSON.stringify({
    kinds,
    title,
    description,
    movements: keptMovements,
    videos: videoRows.map((r) => r.seededID ?? r.text.trim()).filter(Boolean),
  });
  const baseline = useRef(snapshot);
  const dirty =
    placementChanged ||
    snapshot !== baseline.current ||
    (noteReady && noteText !== noteBase.current);

  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmingDiscard(true);
    else onOpenChange(false);
  };
  closeRef.current = requestClose;

  useLayoutEffect(() => {
    if (confirmingDiscard) keepEditingRef.current?.focus();
  }, [confirmingDiscard]);

  const buildOutput = (youTubeIDs: string[]): EditorWorkoutData => {
    const out: EditorWorkoutData & { programmingType?: unknown } = {
      ...initial,
      title: title.trim(),
      description,
      tags: [...kinds],
      youTubeIDs: [...youTubeIDs],
    };
    if (initial.movements !== undefined || keptMovements.length > 0) out.movements = keptMovements;
    // Old card code leaked the program's type into stored card data.
    delete out.programmingType;
    return out;
  };

  const optionTags = [
    ...WORKOUT_CATEGORIES,
    ...(initial.tags ?? []).filter((t) => kindValue(t) === null && t.trim() !== ""),
  ];
  const isOn = (option: string) => kinds.some((t) => canonical(t) === option);
  const toggleKind = (option: string) => {
    setKinds((prev) => {
      if (prev.some((t) => canonical(t) === option)) {
        return prev.filter((t) => canonical(t) !== option);
      }
      return prev.length >= MAX_KINDS ? prev : [...prev, option];
    });
  };

  const onMovementData = (key: number, data: Movement) => {
    setMovementRows((prev) => {
      const row = prev.find((r) => r.key === key);
      if (!row || sameMovement(row.movement, data)) return prev;
      return prev.map((r) =>
        r.key === key ? { ...r, movement: mergeMovement(r.movement, data) } : r
      );
    });
  };

  const addMovement = () => {
    setMovementRows((prev) => {
      const ids = prev
        .map((r) => r.movement.id)
        .filter((id): id is number => typeof id === "number");
      const id = ids.length > 0 ? Math.max(...ids) + 1 : 1;
      return [
        ...prev,
        { key: nextKey.current++, movement: { id, movementRegistryID: "" }, fromInitial: false },
      ];
    });
  };

  const removeMovement = (key: number) =>
    setMovementRows((prev) => prev.filter((r) => r.key !== key));

  const clearVideoError = (key: number) => {
    setVideoErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setFormError(null);
  };

  const changeVideo = (key: number, text: string) => {
    setVideoRows((prev) => prev.map((r) => (r.key === key ? { ...r, text, seededID: null } : r)));
    clearVideoError(key);
  };

  const addVideo = () =>
    setVideoRows((prev) =>
      prev.length >= MAX_VIDEOS
        ? prev
        : [...prev, { key: nextKey.current++, text: "", seededID: null }]
    );

  const removeVideo = (key: number) => {
    setVideoRows((prev) => prev.filter((r) => r.key !== key));
    clearVideoError(key);
  };

  const save = async () => {
    if (saving) return;
    const { ids, errors } = parseVideos(videoRows);
    const missingTitle = title.trim() === "";
    const badVideos = Object.keys(errors).length > 0;
    setTitleError(missingTitle ? "Give the workout a title so members know what it is." : null);
    setVideoErrors(errors);
    setFormError(
      badVideos ? "A video link is not a YouTube link. Fix it or remove it to save." : null
    );
    if (missingTitle) {
      titleRef.current?.focus();
      return;
    }
    if (badVideos) return;

    setSaving(true);
    try {
      await onSave(buildOutput(ids), noteReady ? noteText : undefined);
      onOpenChange(false);
    } catch (err) {
      const userMessage = (err as { userMessage?: unknown } | null)?.userMessage;
      setFormError(
        typeof userMessage === "string"
          ? userMessage
          : isPermissionDenied(err)
            ? "You can't change workouts in this program. Your changes are still here."
            : "We couldn't save the workout. Your changes are still here. Check your connection and try again."
      );
      setSaving(false);
    }
  };

  const preview: WorkoutData = {
    title,
    description,
    tags: kinds,
    movements: keptMovements,
    youTubeIDs: parseVideos(videoRows).ids,
  };

  const titleId = `${uid}-title`;
  const descId = `${uid}-description`;

  return (
    <DialogContent
      className="flex max-h-[calc(100dvh-2rem)] max-w-[min(72rem,calc(100%-2rem))] flex-col gap-0 p-0"
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        titleRef.current?.focus();
      }}
    >
      <DialogHeader className="border-b p-4 pr-12">
        <DialogTitle className="text-base font-semibold">
          {mode === "create" ? "New workout" : "Edit workout"}
        </DialogTitle>
        <div className="flex flex-wrap items-center gap-2">
          <DialogDescription>
            {programName} · {placement}
          </DialogDescription>
          {placementControl}
        </div>
      </DialogHeader>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="grid gap-6 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
          <div className="flex min-w-0 flex-col gap-5">
            <div className="flex flex-col gap-2">
              <Label htmlFor={titleId}>Title</Label>
              <Input
                id={titleId}
                ref={titleRef}
                value={title}
                placeholder="For example: Back squat"
                aria-invalid={titleError ? true : undefined}
                aria-describedby={titleError ? `${titleId}-error` : undefined}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (titleError) setTitleError(null);
                }}
              />
              {titleError && (
                <p id={`${titleId}-error`} role="alert" className="text-sm text-destructive">
                  {titleError}
                </p>
              )}
            </div>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium">
                Tags <span className="font-normal text-muted-foreground">(up to {MAX_KINDS})</span>
              </legend>
              <div className="flex flex-wrap gap-2">
                {optionTags.map((option) => {
                  const on = isOn(option);
                  return (
                    <Button
                      key={option}
                      type="button"
                      variant={on ? "selected" : "outline"}
                      size="sm"
                      className="h-7 rounded-full px-3 text-xs"
                      aria-pressed={on}
                      disabled={!on && kinds.length >= MAX_KINDS}
                      onClick={() => toggleKind(option)}
                    >
                      {kindLabel(option)}
                    </Button>
                  );
                })}
              </div>
            </fieldset>

            <section className="flex flex-col gap-2" aria-labelledby={`${uid}-videos`}>
              <h4 id={`${uid}-videos`} className="text-sm font-medium">
                Videos <span className="font-normal text-muted-foreground">(optional)</span>
              </h4>
              {videoRows.map((row, i) => {
                const error = videoErrors[row.key];
                const errorId = `${uid}-video-${row.key}-error`;
                return (
                  <div key={row.key} className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <Input
                        className="flex-1"
                        aria-label={`Video link ${i + 1}`}
                        placeholder="Paste a YouTube link"
                        value={row.text}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={error ? errorId : undefined}
                        onChange={(e) => changeVideo(row.key, e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove video ${i + 1}`}
                        onClick={() => removeVideo(row.key)}
                      >
                        <IconX />
                      </Button>
                    </div>
                    {error && (
                      <p id={errorId} className="text-sm text-destructive">
                        {error}
                      </p>
                    )}
                  </div>
                );
              })}
              <Button
                type="button"
                variant="outline"
                className="self-start"
                onClick={addVideo}
                disabled={videoRows.length >= MAX_VIDEOS}
              >
                <IconPlus />
                Add video
              </Button>
              <p className="text-xs text-muted-foreground">
                Up to {MAX_VIDEOS} YouTube videos. Members switch between them with the arrows.
              </p>
            </section>

            <div className="flex flex-col gap-2">
              <Label htmlFor={descId}>What members read</Label>
              <Textarea
                id={descId}
                className="min-h-28"
                value={description}
                placeholder="What to do, how to scale it, anything members should know."
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <section className="flex flex-col gap-2" aria-labelledby={`${uid}-movements`}>
              <h4 id={`${uid}-movements`} className="text-sm font-medium">
                Movements
              </h4>
              {movementRows.map((row) => (
                <MovementItem
                  key={row.key}
                  id={row.key}
                  movementData={row.movement}
                  onGetMovementData={(data) => onMovementData(row.key, data)}
                  onRemove={removeMovement}
                  editMode
                />
              ))}
              <Button type="button" variant="outline" className="self-start" onClick={addMovement}>
                <IconPlus />
                Add movement
              </Button>
              <p className="text-xs text-muted-foreground">
                Loads in % use each member&apos;s own best lift, so everyone sees their own weight.
              </p>
            </section>

            {note && (
              <div className="flex flex-col gap-2">
                <Label htmlFor={`${uid}-note`}>
                  Private notes{" "}
                  <span className="font-normal text-muted-foreground">({note.label})</span>
                </Label>
                <Textarea
                  id={`${uid}-note`}
                  className="min-h-20"
                  value={noteText}
                  disabled={!noteReady}
                  maxLength={WORKOUT_NOTE_MAX}
                  placeholder="The goal, what to watch for, how to scale on the day."
                  onChange={(e) => setNoteText(e.target.value)}
                  data-testid="workout-note-input"
                />
                <p
                  className="text-xs text-muted-foreground"
                  role={noteReady ? undefined : "status"}
                >
                  {note.status === "error"
                    ? "We couldn't load your private notes, so they can't be changed right now. Everything else saves as usual."
                    : noteReady
                      ? "Members never see these."
                      : "Loading your private notes…"}
                </p>
              </div>
            )}
          </div>

          <aside className="flex min-w-0 flex-col gap-2" aria-labelledby={`${uid}-preview`}>
            <h4
              id={`${uid}-preview`}
              className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              What a member sees
            </h4>
            <div className="rounded-xl border bg-card p-4 text-card-foreground">
              <p className="mb-2 text-xs text-muted-foreground">
                {placement} · {programName}
              </p>
              <WorkoutPartView workout={{ workoutData: preview }} />
            </div>
            {note && (
              <p className="text-xs text-muted-foreground">
                No private notes here: members never see them.
              </p>
            )}
          </aside>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t bg-muted/50 p-4">
        {formError && (
          <p role="alert" className="text-sm text-destructive">
            {formError}
          </p>
        )}
        {confirmingDiscard ? (
          <div
            role="group"
            aria-labelledby={`${uid}-discard`}
            className="flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <div className="flex-1">
              <p id={`${uid}-discard`} className="font-medium">
                Discard changes?
              </p>
              <p className="text-muted-foreground">Your edits to this workout will be lost.</p>
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button
                ref={keepEditingRef}
                type="button"
                variant="outline"
                onClick={() => setConfirmingDiscard(false)}
              >
                Keep editing
              </Button>
              <Button type="button" variant="destructive" onClick={() => onOpenChange(false)}>
                Discard
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={requestClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        )}
      </div>
    </DialogContent>
  );
};

const WorkoutEditor: React.FC<WorkoutEditorProps> = (props) => {
  const { open, onOpenChange } = props;
  const closeRef = useRef<() => void>(() => onOpenChange(false));
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : closeRef.current())}>
      {open && <EditorContent {...props} closeRef={closeRef} />}
    </Dialog>
  );
};

export default WorkoutEditor;
